import {
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
} from "@chakra-ui/react";
import { safeHTML } from "@lib/html";
import { DEFAULT_LANGUAGE, Language, translate as t } from "@models/language";
import { isEmpty } from "lodash";
import {
  FieldErrors,
  FieldValues,
  RegisterOptions,
  UseFormRegister,
} from "react-hook-form";
import { Field } from "./types";

const DynamicFormCheckbox = ({
  field,
  errors,
  register,
  language: language = DEFAULT_LANGUAGE,
  isDirty: isDirty = true,
  className: className = "",
}: {
  field: Field;
  errors: FieldErrors<FieldValues>;
  register: UseFormRegister<FieldValues>;
  language?: Language;
  isDirty?: boolean;
  className?: string;
}) => {
  // TODO: default translation
  const _t = (key?: string, language = DEFAULT_LANGUAGE) => key || "";

  // https://tailwindcss.com/docs/content-configuration#dynamic-class-names
  let colSpan = "sm:col-span-1";
  switch (field.span) {
    case 1:
      colSpan = "sm:col-span-1";
      break;
    case 2:
      colSpan = "sm:col-span-2";
      break;
    case 3:
      colSpan = "sm:col-span-3";
      break;
    case 4:
      colSpan = "sm:col-span-4";
      break;
  }

  const toRegisterOptions = (
    field: Field
  ): RegisterOptions<FieldValues, string> => {
    return {
      ...(field.required
        ? {
            required:
              t(field.errorMessages?.required, language) ||
              _t("This field is required", language),
          }
        : {}),
    };
  };

  return (
    <>
      <FormControl
        isInvalid={isDirty && !!errors[field.key]}
        className={`${field.span ? colSpan : ""} col-span-1 ${className}`}
        isRequired={
          field.options && !isEmpty(field.options) ? false : field.required
        }
        isDisabled={field.disabled}
        isReadOnly={field.readonly}
      >
        {field.options && !isEmpty(field.options) ? (
          <>
            <FormLabel htmlFor={field.key}>
              {t(field.label, language)}
            </FormLabel>
            <FormHelperText
              dangerouslySetInnerHTML={safeHTML(t(field.note, language))}
            ></FormHelperText>
            <div className="flex flex-col">
              {field.options.map((option, i) => (
                <Checkbox
                  key={i}
                  colorScheme="primary"
                  value={option.value}
                  {...register(field.key)}
                >
                  {t(option.label, language) || option.value}
                </Checkbox>
              ))}
            </div>
          </>
        ) : (
          <Checkbox
            colorScheme="primary"
            {...register(field.key, {
              ...toRegisterOptions(field),
            })}
            name={field.key}
          >
            <span
              dangerouslySetInnerHTML={safeHTML(t(field.label, language))}
            ></span>
            {field.required && <span className="ml-1 text-primary">*</span>}
          </Checkbox>
        )}

        <FormErrorMessage>
          {String(errors[field.key]?.message || "")}
        </FormErrorMessage>
      </FormControl>
    </>
  );
};

export default DynamicFormCheckbox;
